import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query"
import { API_BASE_URL, apiFetch, GET } from "@/api/client"
import type { ReportType } from "@/api/types"

export type SelectedWeek = { isoYear: number; isoWeek: number }

export function reportsOptions() {
	return queryOptions({
		queryKey: ["reports"],
		queryFn: () => GET("/api/v1/reports"),
	})
}

export function useReports() {
	return useQuery(reportsOptions())
}

export function reportOptions(slug: ReportType) {
	return queryOptions({
		queryKey: ["reports", slug],
		queryFn: () =>
			GET("/api/v1/reports/{report_type}", {
				params: { path: { report_type: slug } },
			}),
	})
}

export function useReport(slug: ReportType) {
	return useQuery(reportOptions(slug))
}

export function useReportWeeks(slug: ReportType) {
	return useQuery({
		queryKey: ["reports", slug, "weeks"],
		queryFn: () =>
			GET("/api/v1/reports/{report_type}/weeks", {
				params: { path: { report_type: slug } },
			}),
	})
}

export function useReportPreview(
	slug: ReportType,
	week: SelectedWeek | undefined,
) {
	return useQuery({
		queryKey: ["reports", slug, "preview", week?.isoYear, week?.isoWeek],
		queryFn: async () => {
			const url = new URL(`${API_BASE_URL}/api/v1/reports/preview`)
			url.searchParams.set("report_type", slug)
			if (week) {
				url.searchParams.set("iso_year", String(week.isoYear))
				url.searchParams.set("iso_week", String(week.isoWeek))
			}
			const res = await apiFetch(url)
			return res.text()
		},
		enabled: !!week,
		placeholderData: keepPreviousData,
		retry: false,
	})
}

export function cmiCommentaryOptions(week: SelectedWeek | undefined) {
	return queryOptions({
		queryKey: ["reports", "cmi", "commentary", week?.isoYear, week?.isoWeek],
		queryFn: () =>
			GET("/api/v1/reports/cmi/commentary", {
				params: {
					query: { iso_year: week!.isoYear, iso_week: week!.isoWeek },
				},
			}),
		enabled: week !== undefined,
		placeholderData: keepPreviousData,
	})
}

export function useCmiCommentary(week: SelectedWeek | undefined) {
	return useQuery(cmiCommentaryOptions(week))
}

export function cmiB30Options(week: SelectedWeek | undefined) {
	return queryOptions({
		queryKey: ["reports", "cmi", "b30", week?.isoYear, week?.isoWeek],
		queryFn: () =>
			GET("/api/v1/market-data/cmi/b30", {
				params: {
					query: { iso_year: week!.isoYear, iso_week: week!.isoWeek },
				},
			}),
		enabled: week !== undefined,
	})
}

export function useCmiB30(week: SelectedWeek | undefined) {
	return useQuery(cmiB30Options(week))
}

export function reportRecipientsOptions(slug: ReportType) {
	return queryOptions({
		queryKey: ["reports", slug, "recipients"] as const,
		queryFn: () =>
			GET("/api/v1/reports/{report_type}/recipients", {
				params: { path: { report_type: slug } },
			}),
	})
}

export function useReportRecipients(slug: ReportType) {
	return useQuery(reportRecipientsOptions(slug))
}
